import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { FaCalendarAlt, FaMoneyCheckAlt, FaTimes, FaSearch, FaFileExport, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import axiosInstance from '../../../api/axiosInstance';
import { Helmet } from "react-helmet-async";
import Pagination from '../../Pagination/Pagination';
import './ReminderScholarList.css';

const MODES = ['Cash', 'Cheque', 'UPI', 'Bank Transfer'];
const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

const ReminderScholarList = () => {
  const [scholars, setScholars] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [payModalData, setPayModalData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null); // Track expanded row
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [form, setForm] = useState({
    currentWork: '', scholarType: 'Recurring', amount: '', 
    date: new Date().toISOString().split('T')[0], mode: 'Cash', refNo: ''
  });

  useEffect(() => { fetchScholars(); }, []);

  const fetchScholars = async () => {
    try {
      const res = await axiosInstance.get('/api/scholars');
      setScholars(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (err) { console.error(err); }
  };

  const reminders = useMemo(() => {
    let list = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    scholars.forEach(s => {
      if (s.isDeleted || !s.isActive) return;
      if (!s.scholarships || s.scholarships.length === 0) return;

      const allPayments = [...s.scholarships].sort((a,b) => new Date(b.date) - new Date(a.date));
      const absoluteLatest = allPayments[0];

      if (absoluteLatest.scholarType === 'One Time') return;

      const recurringRecords = allPayments.filter(sch => sch.scholarType === 'Recurring');
      if (recurringRecords.length === 0) return;

      const firstPayment = recurringRecords[recurringRecords.length - 1]; 
      const cycleStart = new Date(firstPayment.date);
      cycleStart.setFullYear(cycleStart.getFullYear() + recurringRecords.length - 1);

      const nextDueDate = new Date(firstPayment.date);
      nextDueDate.setFullYear(nextDueDate.getFullYear() + recurringRecords.length);

      const diffTime = nextDueDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 60) {
        list.push({
          scholarId: s._id, name: s.name, fatherName: s.fatherName, contactNumber: s.contactNumber,
          state: s.state, district: s.district, address: s.address,
          lastWork: absoluteLatest.currentWork, lastAmount: absoluteLatest.amount,
          cycleStart, nextDueDate, diffDays
        });
      }
    });

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(r => r.name.toLowerCase().includes(q) || r.contactNumber.includes(q));
    }

    if (statusFilter === 'Expired') list = list.filter(r => r.diffDays < 0);
    else if (statusFilter === 'Due') list = list.filter(r => r.diffDays >= 0);

    return list.sort((a, b) => a.nextDueDate - b.nextDueDate);
  }, [scholars, searchQuery, statusFilter]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter]);

  const paginatedReminders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return reminders.slice(start, start + itemsPerPage);
  }, [reminders, currentPage, itemsPerPage]);

  const openPayForm = (rem) => {
    setForm({
      currentWork: '', scholarType: 'Recurring', amount: rem.lastAmount ? rem.lastAmount.toString() : '',
      date: new Date().toISOString().split('T')[0], mode: 'Cash', refNo: ''
    });
    setPayModalData(rem);
    setShowAddForm(true);
  };

  const handleAmountChange = (e) => {
    const onlyDigits = e.target.value.replace(/\D/g, '');
    setForm({ ...form, amount: onlyDigits });
  };

  const handleProcessRenewal = async () => {
    if (!form.currentWork || !form.amount || !form.date || !form.mode) 
      return Swal.fire('Warning', 'All fields are mandatory.', 'warning');

    try {
      await axiosInstance.post(`/api/scholars/${payModalData.scholarId}/scholarships`, form);
      
      if (form.scholarType === 'One Time') {
        Swal.fire('Success', 'One Time payment recorded. Scholar removed from reminder list.', 'success');
      } else {
        Swal.fire('Success', 'Renewal added successfully. Next due date updated to next year!', 'success');
      }
      
      setPayModalData(null); 
      setShowAddForm(false);
      fetchScholars();
    } catch (err) { Swal.fire('Error', 'Failed to process renewal', 'error'); }
  };

  const resetForm = () => { setShowAddForm(false); setPayModalData(null); };

  const handleExportExcel = async () => {
    if (reminders.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Reminders');
    // ... Excel Export Logic (Unchanged)
    worksheet.mergeCells('A1:I1');
    worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
    worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.mergeCells('A2:I2');
    worksheet.getCell('A2').value = 'Recurring Scholarships Reminder List';
    worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.addRow([]);
    const headerRow = worksheet.addRow(['S.No', 'Student Name', 'Father Name', 'Contact Number', 'State', 'District', 'Address', 'Expiry Date', 'Status']);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });
    reminders.forEach((r, i) => {
      const statusText = r.diffDays < 0 ? `Expired (${Math.abs(r.diffDays)} days)` : `Due in ${r.diffDays} days`;
      worksheet.addRow([i + 1, r.name, r.fatherName, r.contactNumber, r.state, r.district, r.address, new Date(r.nextDueDate).toLocaleDateString('en-IN'), statusText]);
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Scholar_Reminders.xlsx');
  };

  return (
    <>
    <Helmet>
                <title>Reminder List | Rajput Shiksha Kosh</title>
        
                <meta
                  name="description"
                  content="Manage recurring scholarship reminders for the Rajput Shiksha Kosh organization."
                />
              </Helmet>
    <div className="admin-reminder-container">
      <h2 className="admin-reminder-title"><FaCalendarAlt style={{ marginRight: '8px' }} /> Recurring Beneficiary Reminder</h2>

      <div className="admin-reminder-controls">
        <div className="admin-reminder-search-wrapper">
          <FaSearch className="admin-reminder-search-icon" />
          <input type="text" className="admin-reminder-search-input" placeholder="Search by Name or Contact..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        
        <div className="admin-reminder-action-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="admin-reminder-limit-select">
            <option value="All">All Reminders</option>
            <option value="Expired">Expired Only</option>
            <option value="Due">Due in Next 60 Days</option>
          </select>
          <select className="admin-reminder-limit-select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} / page</option>)}
          </select>
          <button className="admin-reminder-btn admin-reminder-btn-export" onClick={handleExportExcel} title="Export Styled Excel">
            <FaFileExport /> Export Excel
          </button>
        </div>
      </div>

      {/* --- POPUP MODAL --- */}
      {showAddForm && payModalData && (
        <div className="admin-reminder-modal-overlay" onClick={resetForm}>
          <div className="admin-reminder-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="admin-reminder-modal-close" onClick={resetForm}><FaTimes /></button>
            <h3 className="admin-reminder-form-title">Pay Renewal</h3>

            <div className="admin-reminder-info-card">
              <FaInfoCircle className="admin-reminder-info-icon"/>
              <div className="admin-reminder-info-details">
                <p><b>Student:</b> {payModalData.name} <span className="admin-reminder-sub-text">(S/O {payModalData.fatherName})</span></p>
                <p><b>Contact:</b> {payModalData.contactNumber}</p>
                <p><b>Cycle Period:</b> <span className="admin-reminder-highlight-date">{new Date(payModalData.cycleStart).toLocaleDateString('en-IN')}</span> to <span className="admin-reminder-highlight-date">{new Date(payModalData.nextDueDate).toLocaleDateString('en-IN')}</span></p>
              </div>
            </div>
              
            <div className="admin-reminder-form-grid">
              <div className="admin-reminder-form-group">
                <label>Previous Work / Class</label>
                <input type="text" value={payModalData.lastWork} readOnly className="admin-reminder-readonly-input" />
              </div>
              <div className="admin-reminder-form-group">
                <label>Current Work / Class <span className="admin-reminder-req">*</span></label>
                <input type="text" value={form.currentWork} onChange={e => setForm({...form, currentWork: e.target.value})} placeholder="Enter updated class/work" />
              </div>
              
              <div className="admin-reminder-form-group admin-reminder-col-span-full">
                <label>Payment Type <span className="admin-reminder-req">*</span></label>
                <select value={form.scholarType} onChange={e => setForm({...form, scholarType: e.target.value})}>
                  <option value="Recurring">Recurring (Extends Cycle by 1 Year)</option>
                  <option value="One Time">One Time (Removes from Recurring Reminder)</option>
                </select>
              </div>

              <div className="admin-reminder-form-group">
                <label>Previous Amount (₹)</label>
                <input type="text" value={payModalData.lastAmount} readOnly className="admin-reminder-readonly-input" />
              </div>
              <div className="admin-reminder-form-group">
                <label>New Amount Paid (₹) <span className="admin-reminder-req">*</span></label>
                <input type="text" value={form.amount} onChange={handleAmountChange} placeholder="Enter Amount Paid" />
              </div>
              
              <div className="admin-reminder-form-group">
                <label>Amount Paid Date <span className="admin-reminder-req">*</span></label>
                <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
              </div>
              <div className="admin-reminder-form-group">
                <label>Payment Mode <span className="admin-reminder-req">*</span></label>
                <select value={form.mode} onChange={e => setForm({...form, mode: e.target.value})}>
                  {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              
              {form.mode !== 'Cash' && (
                <div className="admin-reminder-form-group admin-reminder-col-span-full">
                  <label>Ref / Txn No <span className="admin-reminder-req">*</span></label>
                  <input type="text" value={form.refNo} onChange={e => setForm({...form, refNo: e.target.value})} placeholder="Transaction Reference" />
                </div>
              )}
            </div>
              
            <div className="admin-reminder-form-buttons">
              <button className="admin-reminder-btn-cancel" onClick={resetForm}>Cancel</button>
              <button className="admin-reminder-btn-confirm" onClick={handleProcessRenewal}>Confirm Payment</button>
            </div>
          </div>
        </div>
      )}

      {/* --- RESPONSIVE TABLE --- */}
      <div className="admin-reminder-table-wrapper">
        <table className="admin-reminder-table">
          <thead>
            <tr>
              <th className="col-sno">S.No</th>
              <th className="col-name admin-reminder-align-left">Student Info</th>
              <th className="col-contact">Contact Number</th>
              <th className="col-class">Class/Work</th>
              <th className="col-expiry">Expiry Date</th>
              <th className="col-status">Status</th>
              <th className="col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedReminders.length === 0 ? <tr><td colSpan="7" style={{padding:'40px', textAlign:'center', color: '#64748b'}}>No pending renewals found.</td></tr> : 
              paginatedReminders.map((r, i) => {
                const globalIndex = (currentPage - 1) * itemsPerPage + i;
                const statusBadge = r.diffDays < 0 
                  ? <span className="admin-reminder-badge expired">Expired ({Math.abs(r.diffDays)} days)</span> 
                  : <span className="admin-reminder-badge due">Due in {r.diffDays} days</span>;

                return (
                  <React.Fragment key={r.scholarId}>
                    <tr className="admin-reminder-main-row">
                      <td className="col-sno">{globalIndex + 1}</td>
                      <td className="col-name admin-reminder-align-left">
                        <div className="admin-reminder-name-cell">
                          <div>
                            <span style={{fontWeight: 600}}>{r.name}</span><br/>
                            <span style={{fontSize:'0.75rem', color:'#64748b'}}>S/O {r.fatherName}</span>
                          </div>
                          <button 
                            className="admin-reminder-expand-btn" 
                            onClick={() => setExpandedRow(expandedRow === r.scholarId ? null : r.scholarId)}
                          >
                            {expandedRow === r.scholarId ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>
                      <td className="col-contact">{r.contactNumber}</td>
                      <td className="col-class">{r.lastWork}</td>
                      <td className="col-expiry" style={{fontWeight: 700, color: '#e67e22'}}>{new Date(r.nextDueDate).toLocaleDateString('en-IN')}</td>
                      <td className="col-status">{statusBadge}</td>
                      <td className="col-action">
                        <button className="admin-reminder-btn-pay" onClick={() => openPayForm(r)}>
                          <FaMoneyCheckAlt /> Pay Renewal
                        </button>
                      </td>
                    </tr>

                    {/* EXPANDED ROW FOR MOBILE/TABLET */}
                    {expandedRow === r.scholarId && (
                      <tr className="admin-reminder-expanded-row">
                        <td colSpan="7">
                          <div className="admin-reminder-expanded-content">
                            <div className="exp-item exp-contact">
                              <span className="exp-label">Contact:</span>
                              <span className="exp-value">{r.contactNumber}</span>
                            </div>
                            <div className="exp-item exp-class">
                              <span className="exp-label">Class/Work:</span>
                              <span className="exp-value">{r.lastWork}</span>
                            </div>
                            <div className="exp-item exp-expiry">
                              <span className="exp-label">Expiry Date:</span>
                              <span className="exp-value" style={{fontWeight: 700, color: '#e67e22'}}>{new Date(r.nextDueDate).toLocaleDateString('en-IN')}</span>
                            </div>
                            <div className="exp-item exp-status">
                              <span className="exp-label">Status:</span>
                              <span className="exp-value">{statusBadge}</span>
                            </div>
                            <div className="exp-item exp-action">
                              <span className="exp-label">Action:</span>
                              <span className="exp-value">
                                <button className="admin-reminder-btn-pay" onClick={() => openPayForm(r)}>
                                  <FaMoneyCheckAlt /> Pay Renewal
                                </button>
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
      
      {reminders.length > 0 && (
        <Pagination totalItems={reminders.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
      )}
    </div>
    </>
  );
};

export default ReminderScholarList;